import { useState } from 'react';

type Status = 'details' | 'compose' | 'busy' | 'received';
type Created = {ok:true;from:string;to:string;subject:string;body:string;stampDataUrl:string;status:string};
const API_BASE = '/api/playground/stage1';
async function submit<T>(route: string, body: object): Promise<T> {
  const response = await fetch(`${API_BASE}/${route}`, {
    method: 'POST', credentials: 'same-origin', headers: {'Content-Type':'application/json'},
    body: JSON.stringify(body)
  });
  const data = await response.json();
  if (!response.ok || data.ok === false) throw new Error(data.error || `HTTP ${response.status}`);
  return data as T;
}
export default function LiveVerificationStage() {
  const [firstName,setFirstName] = useState('');
  const [lastName,setLastName] = useState('');
  const [email,setEmail] = useState('');
  const [session,setSession] = useState('');
  const [status,setStatus] = useState<Status>('details');
  const [body,setBody] = useState('Hello, this is my bioVerifier test message.');
  const [created,setCreated] = useState<Created|null>(null);
  const [error,setError] = useState('');
  const [showInbox,setShowInbox] = useState(false);
  async function prepare() {
    setError('');
    setStatus('busy');
    try {
      const result = await submit<{ok:true;sessionId:string}>('session',{firstName,lastName,email});
      setSession(result.sessionId);
      setStatus('compose');
    } catch(err) {setError(String((err as Error).message));setStatus('details');}
  }
  async function send() {
    setError('');setStatus('busy');
    try {
      const result = await submit<Created>('send',{sessionId:session,body});
      setCreated(result);setShowInbox(true);setStatus('received');
    } catch(err) {setError(String((err as Error).message));setStatus('compose');}
  }
  const field='w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:border-blue-600 focus:outline-none';
  const button='rounded-full bg-blue-700 px-6 py-3 text-white font-semibold disabled:opacity-50';
  return (
    <section id="real-verification-playground" className="w-full px-4 md:px-8 lg:px-12 py-14 bg-white">
      <div className="max-w-5xl mx-auto">
        <p className="uppercase tracking-widest text-sm font-semibold text-blue-700 mb-2">Stage 1 · Live V2 generation</p>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Create your own bioStamp demonstration</h2>
        <p className="text-gray-600 mb-8">Prepare a virtual message and generate a real server-registered V2 bioStamp. No extension is required for this step. Verification and tamper testing will only be enabled once the genuine server verification adapter is connected.</p>
        <div className="rounded-2xl border border-gray-200 p-5 md:p-8 shadow-sm space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block text-sm font-medium text-gray-700">First name<input className={`${field} mt-1`} maxLength={80} disabled={status!=='details'} value={firstName} onChange={e=>setFirstName(e.target.value)}/></label>
            <label className="block text-sm font-medium text-gray-700">Last name<input className={`${field} mt-1`} maxLength={80} disabled={status!=='details'} value={lastName} onChange={e=>setLastName(e.target.value)}/></label>
          </div>
          <label className="block text-sm font-medium text-gray-700">Your email address<input type="email" className={`${field} mt-1`} maxLength={254} disabled={status!=='details'} value={email} onChange={e=>setEmail(e.target.value)}/></label>
          <p className="text-xs text-gray-500">Contact details are used only for this short-lived demonstration session. Marketing signup will be added separately with explicit consent.</p>
          {status==='details' && <button className={button} type="button" disabled={!firstName.trim()||!lastName.trim()||!email.includes('@')} onClick={prepare}>Create bioStamp</button>}
          {(status==='compose'||status==='received'||status==='busy') && <div className="space-y-4 border-t pt-5">
            <div className="flex items-center gap-3"><div className="h-20 w-20 rounded-xl border-2 border-dashed border-blue-400 bg-blue-50 flex items-center justify-center text-xs text-center text-blue-700 p-2">Provisional stamp</div><div><strong>Compose your message</strong><p className="text-sm text-gray-600">The final stamp will only be registered when you select Send.</p></div></div>
            <label className="block text-sm font-medium text-gray-700">Message body<textarea rows={5} className={`${field} mt-1`} maxLength={1000} disabled={status!=='compose'} value={body} onChange={e=>setBody(e.target.value)}/></label>
            {status==='compose' && <button className={button} type="button" disabled={!body.trim()} onClick={send}>Send to virtual inbox</button>}
            {status==='busy' && <p role="status" className="text-blue-700">Processing your secure request…</p>}
          </div>}
          {error && <p role="alert" className="text-red-700">{error}</p>}
        </div>
        {showInbox && created && <div className="mt-6 rounded-2xl border border-blue-200 shadow-sm overflow-hidden">
          <div className="bg-blue-50 px-6 py-4 font-semibold text-blue-900">Virtual inbox — received message</div>
          <div className="p-6 space-y-4">
            <div className="text-sm"><p><b>From:</b> {created.from}</p><p><b>To:</b> {created.to}</p><p><b>Subject:</b> {created.subject}</p></div>
            <div className="border-t pt-4 whitespace-pre-wrap break-words">{created.body}</div>
            <img className="w-40 h-auto" alt="Genuine V2 registered bioStamp" src={created.stampDataUrl}/>
            <p role="status" className="text-amber-800 text-sm">V2 stamp registered; recipient-side verification has not yet been performed.</p>
            <div className="flex flex-wrap gap-3"><button className={button} type="button" disabled title="Awaiting genuine V2 verification integration">Verify Sender</button><button className={button} type="button" disabled>Alter Message Text</button><button className={button} type="button" disabled>Alter bioStamp</button><button className={button} type="button" disabled>Send Manipulated Message</button></div>
          </div>
        </div>}
      </div>
    </section>
  );
}
